import { doc, writeBatch } from 'firebase/firestore';

// Issuing a notice, in one place.
//
// Two modules need it now: Notice & Payment, and OLE Management, where issuing a notice for an
// activity should carry that activity's dates and participants instead of making staff retype them.
//
// Shared rather than copied. A second fan-out implementation is exactly the shape that produced four
// separate "authorised staff" lists elsewhere in this platform -- and this particular piece of code
// has already had both a roster leak and a timeout, so having two of it is not a risk worth taking.

// The two modules build their student lookup differently -- Notice & Payment keeps guardianEmail
// and guardianEmail2 as separate fields, OLE Management keeps a guardianEmails array. Accepting both
// here is cheaper and safer than making one of them rebuild its list, and a normaliser in one place
// is exactly the kind of thing a shared module is for.
const guardiansOf = (st) => (
  Array.isArray(st?.guardianEmails)
    ? st.guardianEmails
    : [st?.guardianEmail, st?.guardianEmail2]
).filter(Boolean);

// The addresses that may read a notice: each student and their guardians.
export const audienceFor = (studentBySid, sids) => {
  const out = new Set();
  (sids || []).forEach((sid) => {
    const st = studentBySid[sid];
    if (!st) return;
    if (st.email) out.add(st.email);
    guardiansOf(st).forEach((g) => out.add(g));
  });
  return [...out];
};

// One row per (thing, student).
//
// A rule decides which DOCUMENTS you get, never which fields -- so a notice carrying the whole year
// group's addresses could not be shown to one family without showing them the roster. Hence a row
// per student, naming exactly one.
// merge: true rewrites only the fields given, leaving the rest of an existing row alone. That is
// what an edit to an already-issued notice needs -- rewriting the row wholesale would reset every
// family's signature to unsigned because the drafter fixed a typo.
export const fanOutRows = async ({
  db, schoolId, collectionName, keyPrefix, sids, studentBySid, rowFor, merge = false,
}) => {
  const chunkSize = 400;   // Firestore caps a batch at 500 writes.
  for (let i = 0; i < sids.length; i += chunkSize) {
    const batch = writeBatch(db);
    sids.slice(i, i + chunkSize).forEach((sid) => {
      const st = studentBySid[sid];
      batch.set(
        doc(db, 'schools', schoolId, collectionName, `${keyPrefix}__${sid}`),
        {
          sid,
          studentEmail: st?.email || '',
          // Always written, even empty: a missing field makes the read rule error, and an erroring
          // rule denies -- the row would be invisible to the very people it is for.
          guardianEmails: guardiansOf(st),
          ...rowFor(sid),
        },
        { merge },
      );
    });
    await batch.commit();
  }
};
